import { useRef, useState, useLayoutEffect, useMemo } from 'react';
import { ItemElement } from '../types';
import {
  getItemKey,
  calculateVisibleItems,
  cacheItemPositions,
  hasVisibleItemsChanged,
} from '../utils/visibilityCalculations';

type Item = { value: string; key?: string };
type Items = Item[];

interface UseItemVisibilityOptions {
  items: Items;
  offsetWidth: number;
  withoutCrop?: boolean;
  maxWidth?: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

interface UseItemVisibilityResult {
  visibleIds: string[];
  itemRefs: React.MutableRefObject<Map<string, ItemElement>>;
  hasMore: boolean;
}

/**
 * Custom hook for managing item visibility in a cropped row.
 * Uses position caching to determine which items overflow the container width.
 */
export function useItemVisibility({
  items,
  offsetWidth,
  withoutCrop,
  maxWidth,
  containerRef,
}: UseItemVisibilityOptions): UseItemVisibilityResult {
  const itemRefs = useRef<Map<string, ItemElement>>(new Map());
  // Cache item positions on first render (when all items are visible)
  // This is needed because display:none items have 0 width and can't be measured
  const itemPositionsRef = useRef<Map<string, { left: number; right: number }>>(
    new Map(),
  );

  // Compute item keys once per items change
  const { itemsKey, allItemKeys } = useMemo(() => {
    const keys = items.map(getItemKey);
    return { itemsKey: keys.join('.'), allItemKeys: keys };
  }, [items]);

  const [visibleIds, setVisibleIds] = useState<string[]>(() => allItemKeys);

  // Track previous values to detect changes
  const prevItemsKeyRef = useRef<string>(itemsKey);
  const prevMaxWidthRef = useRef<number | undefined>(maxWidth);

  // Position caching and visibility calculation
  useLayoutEffect(() => {
    // When withoutCrop is true, show all items
    if (withoutCrop) {
      if (hasVisibleItemsChanged(allItemKeys, visibleIds)) {
        setVisibleIds(allItemKeys);
      }
      return undefined;
    }

    if (!containerRef.current) {
      return undefined;
    }

    // Detect if items changed - clear cache and reset visibility
    const itemsChanged = prevItemsKeyRef.current !== itemsKey;
    if (itemsChanged) {
      prevItemsKeyRef.current = itemsKey;
      itemPositionsRef.current.clear();
      // Clean up stale refs for removed items
      const validKeys = new Set(allItemKeys);
      itemRefs.current.forEach((_, key) => {
        if (!validKeys.has(key)) {
          itemRefs.current.delete(key);
        }
      });
      // Reset to all visible so new items can be measured
      setVisibleIds(allItemKeys);
      return undefined;
    }

    // Detect if maxWidth increased - clear cache and reset visibility
    const maxWidthIncreased =
      maxWidth && prevMaxWidthRef.current && maxWidth > prevMaxWidthRef.current;

    if (maxWidthIncreased) {
      itemPositionsRef.current.clear();
      // Reset visibleIds so hidden items become visible and can be measured
      const needsReset = allItemKeys.some((key) => !visibleIds.includes(key));
      if (needsReset) {
        setVisibleIds(allItemKeys);
        prevMaxWidthRef.current = maxWidth;
        return undefined;
      }
    }
    prevMaxWidthRef.current = maxWidth;

    // If cache is empty, we need to measure all items first
    // Items must be visible (not display:none) to measure them
    if (itemPositionsRef.current.size === 0) {
      cacheItemPositions(itemRefs.current, itemPositionsRef.current);

      // If still no cached positions, items might not be rendered yet
      if (itemPositionsRef.current.size === 0) {
        return undefined;
      }
    }

    // Get the constraining width - use maxWidth prop if provided,
    // otherwise get it from grandparent (the outerWrapper in TabBar)
    // since with inline-flex wrapper, our own width grows to fit content
    let constrainedWidth: number;
    if (maxWidth) {
      constrainedWidth = maxWidth;
    } else {
      const grandparent = containerRef.current.parentElement?.parentElement;
      constrainedWidth = grandparent?.getBoundingClientRect().width || 0;
    }

    // Skip if we can't determine width
    if (constrainedWidth === 0) {
      return undefined;
    }

    const newVisibleIds = calculateVisibleItems(
      allItemKeys,
      itemPositionsRef.current,
      constrainedWidth,
      offsetWidth,
    );

    // Only update state if the visible items actually changed
    if (hasVisibleItemsChanged(newVisibleIds, visibleIds)) {
      setVisibleIds(newVisibleIds);
    }

    return undefined;
  }, [
    itemsKey,
    allItemKeys,
    offsetWidth,
    withoutCrop,
    maxWidth,
    visibleIds,
    containerRef,
  ]);

  const hasMore = visibleIds.length !== items.length;

  return {
    visibleIds,
    itemRefs,
    hasMore,
  };
}

export default useItemVisibility;
